import * as R from 'ramda';
import { useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
// component
import { ModalComponent } from '../modal';
// constants
import * as C from '../../constants';
// utilities
import * as U from '../../utilities';
// ui
import { Box, Flex, RelativeFlex } from '../../ui';
// components
import SortList from './components/sort-list';
import FieldList from './components/field-list';
import FilterList from './components/filter-list';
import ReportFormButtons from './components/form-buttons';
import { withDnDStartEnd } from './components/field-list/hocs';
import {
  buttons,
  buildReport,
  isNotReportValid,
} from '../report-format-panel/helpers';
// forms
import { ReportForm } from '../../forms';
//////////////////////////////////////////////////

const onDragEnd = (result, props) => {
  const { destination } = result;

  const { handleDragEnd, handleSortDragEnd, handleReportFieldSortDragEnd } = props;

  if (R.equals(R.prop('droppableId', destination), 'sortFields')) {
    handleSortDragEnd(result);
  } else if (R.equals(R.prop('droppableId', destination), 'reportFields')) {
    handleReportFieldSortDragEnd(result);
  }

  handleDragEnd(result);
};

const EditReportForm = withDnDStartEnd(props => {
  const {
    theme,
    closeModal,
    usedReport,
    cancelAction,
    hideSaveAsNew,
    reportSortList,
    handleDragStart,
    reportFieldList,
    setReportSortList,
    setReportFieldList,
    removeReportRequest,
    updateReportRequest,
  } = props;

  const [opened, setOpened] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [reportFilterGroups, setReportFilterGroups] = useState([]);

  const isEditMode = U.isTrue(editMode);
  const modalTitle = U.ifElse(
    R.not(isEditMode),
    U.getTranslation('actions.createNewReport', 'Create New Report'),
    U.getTranslation('actions.editReport', 'Edit Report'),
  );

  const handleCloseModal = () => {
    if (U.isTrue(editMode)) {
      setEditMode(false);
    }

    setOpened(false);
  };

  const handleRemoveAction = () => {
    const reportUUID = R.path([C.FIELD_UUID], usedReport);

    removeReportRequest(reportUUID);
    handleCloseModal();
    closeModal();
  };

  const handleCreateReport = values => {
    const { closeModal, createReportRequest } = props;

    const newReport = {
      ...buildReport({
        ...props,
        isCreate: true,
        reportSortList,
        reportFieldList,
        reportFilterGroups,
      }),
      ...values,
      uuid: null,
      version: null,
    };

    if (isNotReportValid(newReport)) return;

    createReportRequest(newReport);

    handleCloseModal();
    closeModal();
  };

  const setSubmitButtonStatus = ({ usedReport }) => () => {
    const reportUUID = U.getOrElse(usedReport, C.FIELD_UUID, 'draft');

    return (
      R.or(
        reportUUID.includes('Default'),
        R.equals(reportUUID, 'draft'),
      )
    );
  };

  const handleEditReport = values => {
    const newReport = {
      ...buildReport({
        ...props,
        isUpdate: true,
        reportSortList,
        reportFieldList,
        reportFilterGroups,
      }),
      ...values,
    };

    if (isNotReportValid(newReport)) return;

    updateReportRequest(newReport);

    handleCloseModal();
    closeModal();
  };

  const handleClickSave = () => {
    if (U.isNilOrEmpty(R.prop(C.FIELD_NAME, R.head(reportFieldList)))) {
      U.showToastrMessageSimple('error', U.getTranslation('errors.selectAtLeastOneField', 'Please, select at least one field'));
    } else {
      setEditMode(true);
      setOpened(true);
    }
  };

  const handleClickSaveAsNew = () => {
    if (U.isNilOrEmpty(R.prop(C.FIELD_NAME, R.head(reportFieldList)))) {
      U.showToastrMessageSimple('error', U.getTranslation('errors.selectAtLeastOneField', 'Please, select at least one field'));
    } else {
      setEditMode(false);
      setOpened(true);
    }
  };

  return (
    <Box height='85vh'>
      <Box
        overflow='auto'
        minHeight={350}
        bg={theme.colors.white}
        height='calc(100% - 35px)'
      >
        <RelativeFlex
          zIndex={1}
          maxWidth={1520}
          overflowY='visible'
          width='max-content'
          borderTop='1px solid'
          alignItems='flex-start'
          borderColor={theme.colors.middleGrey}
        >
          <DragDropContext onDragStart={handleDragStart} onDragEnd={result => onDragEnd(result, props)}>
            <FieldList
              {...props}
              reportFieldList={reportFieldList}
              setReportFieldList={setReportFieldList}
            />
            <Flex flexDirection='column'>
              <SortList
                {...props}
                reportSortList={reportSortList}
                setReportSortList={setReportSortList}
              />
            </Flex>
          </DragDropContext>
          <FilterList
            {...props}
            reportFilterGroups={reportFilterGroups}
            setReportFilterGroups={setReportFilterGroups}
          />
        </RelativeFlex>
      </Box>
      <ReportFormButtons
        {...props}
        pl={15}
        width='auto'
        style={{ position: 'absolute' }}
        handleClickCancel={cancelAction}
        handleClickSave={handleClickSave}
        showSaveAsNew={R.not(hideSaveAsNew)}
        buttons={buttons(handleRemoveAction)}
        submitting={setSubmitButtonStatus(props)}
        handleClickSaveAsNew={handleClickSaveAsNew}
        hideFormSaveBtn={U.isNilOrEmpty(usedReport)}
        btnText={U.getTranslation('actions.editReport', 'Edit Report')}
      />
      <ModalComponent
        opened={opened}
        modalTitle={modalTitle}
        handleClose={handleCloseModal}
        component={(
          <ReportForm
            isLoading={false}
            isEditMode={isEditMode}
            cancelAction={handleCloseModal}
            title={isEditMode ? 'edit' : 'save'}
            submitAction={isEditMode ? handleEditReport : handleCreateReport}
            submitText={U.getTranslation(`actions.${isEditMode ? 'edit' : 'save'}`)}
            initialValues={isEditMode ? { [C.FIELD_NAME]: R.pathOr('', [C.FIELD_NAME], usedReport) } : ''}
          />
        )}
      />
    </Box>
  );
});

export default EditReportForm;
